import React from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { getAllOrganizationRolesExcludingSystem } from '~/components/communications/ContactUtils';
import ContactTextFieldFormik from '~/components/ContactTextFieldFormik';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import Text from '~/components/core/TextComponents/Text';
import useOrganization from '~/components/OrganizationContext';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import { reportAxiosError } from '~/Utils';

interface DeactivateContactDialogProps {
  contact: ContactFullModel;
  organizationId: number;
  onClose: () => void;
  onDeactivated: () => void;
}

const getValidationSchema = () =>
  Yup.object({
    reason: Yup.string().trim().required('Reason is required'),
  });

const DeactivateContactDialog: React.FC<DeactivateContactDialogProps> = ({
  contact,
  organizationId,
  onClose,
  onDeactivated,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationContactRolesDict } = useOrganization();
  const acceptedRoles = getAllOrganizationRolesExcludingSystem(organizationContactRolesDict);

  const handleDeactivateSubmit = async (
    values: { reason: string; reference_contact_id: string; reference_contact_full_name: string },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      await axios.post(`/api/v1/organizations/${organizationId}/contacts/${contact.id}/deactivate`, {
        reason: values.reason.trim(),
        ...(values.reference_contact_id ? { reference_contact_id: values.reference_contact_id } : {}),
      });
      onDeactivated();
    } catch (error) {
      await reportAxiosError(error);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{ reason: '', reference_contact_id: '', reference_contact_full_name: '' }}
      validationSchema={getValidationSchema()}
      onSubmit={handleDeactivateSubmit}
    >
      {({ handleSubmit, isSubmitting }) => (
        <CardDialog
          isDialog
          title="Deactivate Contact"
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          footerActions={
            <DialogFooterActions
              primaryLabel="Deactivate Contact"
              onClickPrimary={handleSubmit}
              onClickSecondary={onClose}
              disabled={isSubmitting}
            />
          }
        >
          <div className="flex flex-col gap-20">
            <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {`Deactivating "${contact.full_name}" will mark this contact as inactive. Please provide a reason and optionally select a reference contact.`}
            </Text>
            <TextFieldFormik id="reason" label="Reason" fullWidth autoFocus />
            <ContactTextFieldFormik
              id="reference_contact"
              label="Reference Contact"
              acceptedRoles={acceptedRoles}
              outOfContactsContextSearch
              excludedContactIdsList={[contact.id]}
              fixedSearchResults
              fullWidth
            />
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default DeactivateContactDialog;
